import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from 'apiConfig';
import { authAPI } from '../api';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User>;
  signup: (email: string, password: string, full_name?: string, timezone?: string, invite_code?: string, practice_profile?: any) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');

      if (storedToken) {
        try {
          const response = await axios.get<User>(
            `${API_BASE_URL}/auth/me`,
            {
              headers: { Authorization: `Bearer ${storedToken}` },
            }
          );

          setUser(response.data);
          setToken(storedToken);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setIsAuthenticated(false);
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    const response = await axios.post<{ access_token: string; user: User }>(
      `${API_BASE_URL}/auth/login`,
      { email, password }
    );

    const { access_token, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setIsAuthenticated(true);

    return userData;
  };

  const signup = async (
    email: string,
    password: string,
    full_name?: string,
    timezone?: string,
    invite_code?: string,
    practice_profile?: any
  ): Promise<User> => {
    const specialty =
      practice_profile?.specialty_id ||
      practice_profile?.specialty ||
      'general';

    return authAPI.signup({
      email,
      password,
      full_name,
      specialty,
    });
  };

  const logout = async (): Promise<void> => {
    try {
      if (localStorage.getItem('token')) {
        await authAPI.logout();
      }
    } catch {
      // Local logout must still complete if the server hint fails or the token expired.
    }

    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshUser = async (): Promise<void> => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;
    try {
      const response = await axios.get<User>(
        `${API_BASE_URL}/auth/me`,
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      setUser(response.data);
    } catch {
      // Silently fail — stale user data is better than clearing session
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    signup,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
